import { useEffect, useRef, type RefObject } from "react";

/**
 * Keyboard containment for a modal surface (WCAG 2.1 AA — 2.4.3 Focus Order, and the intent of 2.1.2 No
 * Keyboard Trap: the *page behind* must be unreachable while the dialog is up, and the dialog itself must
 * always be leaveable via its own controls or Escape).
 *
 * `aria-modal="true"` is a promise to assistive technology that everything outside the dialog is inert. The
 * browser does not keep that promise on its own: without this hook Tab walks straight out of the dialog into
 * the page behind it, so a screen-reader user is told the background is gone while their keyboard is standing
 * in it. axe cannot see the difference — a missing focus cycle is not machine-checkable — which is why this is
 * one shared primitive with a written E2E test rather than a line in an audit.
 *
 * Used by `Modal` (every `aria-modal` dialog in the SPA goes through it — see
 * `src/ui/modal-primitive.test.mjs`, the guardrail that keeps the seventh dialog from hand-rolling its own)
 * and by the app shell's mobile navigation drawer, which is modal in behaviour without being a dialog.
 */

// Everything the platform puts in the tab order. The dialog container itself carries tabIndex={-1} so it is
// deliberately NOT matched here: it is a focus target on open, never a stop in the cycle.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "audio[controls]",
  "video[controls]",
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/** Is this element actually reachable by Tab? `display:none` has no boxes; `visibility:hidden` is skipped. */
function isTabbable(el: HTMLElement): boolean {
  if (el.getClientRects().length === 0) return false;
  return window.getComputedStyle(el).visibility !== "hidden";
}

/**
 * Where focus lands when the trap engages:
 *   "container" — the dialog itself (tabIndex={-1}); the reading order then starts at its heading. The right
 *                 default for a confirmation, where the first control is often the destructive one.
 *   "first"     — the first focusable control inside.
 *   a ref       — a specific control (a form dialog's first field).
 */
export type InitialFocus = "container" | "first" | RefObject<HTMLElement | null>;

export interface FocusTrapOptions {
  /** Engage the trap. False leaves the keyboard entirely alone (nothing is bound, nothing is focused). */
  active?: boolean;
  /** Escape handler. Omitted → Escape is not intercepted. */
  onEscape?: () => void;
  /** False suspends Escape without tearing the trap down — a dialog mid-write must not vanish under a keystroke. */
  escapeEnabled?: boolean;
  /** Where focus goes on open (default: the container). */
  initialFocus?: InitialFocus;
  /**
   * On close, hand the keyboard back to whatever was focused when the trap engaged (default true) — but only
   * when focus is still inside the trap or was orphaned onto <body> by the unmount. A caller that has already
   * moved focus somewhere deliberate (e.g. back to the save button after a refused save) is never overridden.
   */
  restoreFocus?: boolean;
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  options: FocusTrapOptions = {},
): void {
  const {
    active = true,
    onEscape,
    escapeEnabled = true,
    initialFocus = "container",
    restoreFocus = true,
  } = options;

  // The volatile options live in refs so a re-render (busy → idle, a new inline callback) never re-runs the
  // effect: re-running it would re-move focus out from under whoever is typing in the dialog.
  const latest = useRef({ onEscape, escapeEnabled, initialFocus, restoreFocus });
  latest.current = { onEscape, escapeEnabled, initialFocus, restoreFocus };

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const tabbables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isTabbable);

    // Move focus in, and then CHECK that it arrived. A ref target that has not rendered falls back to the
    // container — and so does one that REFUSES the focus, which the `??` alone could not cover: `focus()`
    // on a disabled control is a silent no-op, so a non-null ref to a field the dialog disables on its
    // opening render left the keyboard standing on the button that opened the dialog, outside a surface
    // whose `aria-modal="true"` had just promised the page behind was inert (found 20.10.2026 on
    // changePrograms/ChangeRoleGrants, whose grantee <select> is disabled until ADR-0016 §4.8's person read
    // answers). The post-check is what makes the promise above hold for a target of any kind, rather than
    // for the two the `??` happened to catch.
    const target = latest.current.initialFocus;
    if (target === "first") (tabbables()[0] ?? container).focus();
    else if (target === "container") container.focus();
    else target.current?.focus();
    if (!container.contains(document.activeElement)) container.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const { onEscape: escape, escapeEnabled: enabled } = latest.current;
        if (escape && enabled) {
          e.preventDefault();
          escape();
        }
        return;
      }
      if (e.key !== "Tab") return;

      const items = tabbables();
      const activeEl = document.activeElement as HTMLElement | null;
      // Nothing to cycle through (a dialog of pure text, or every control disabled mid-write): keep the
      // keyboard on the dialog rather than letting it step into the page behind.
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];

      // Focus somehow outside the dialog (it opened while focus sat elsewhere) — pull it back in.
      if (activeEl === null || !container.contains(activeEl)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      // The two ends of the cycle. Shift+Tab off the container (which precedes its contents in the DOM)
      // would otherwise leave the dialog backwards.
      if (e.shiftKey && (activeEl === first || activeEl === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (!latest.current.restoreFocus) return;
      const activeEl = document.activeElement as HTMLElement | null;
      const orphaned = activeEl === null || activeEl === document.body || container.contains(activeEl);
      if (orphaned && previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [active, containerRef]);
}
